"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const glassPanelVariants = cva(
  "rounded-xl border transition-all duration-300",
  {
    variants: {
      variant: {
        default: "border-slate-200 bg-white shadow-sm",
        elevated: "border-slate-200 bg-white shadow-lg",
        brand:
          "border-primary/20 bg-gradient-to-br from-primary/5 to-primary/[0.02]",
        purple:
          "border-violet-200 bg-gradient-to-br from-violet-50 to-violet-50/50",
        success:
          "border-emerald-200 bg-gradient-to-br from-emerald-50 to-emerald-50/50",
        warning:
          "border-amber-200 bg-gradient-to-br from-amber-50 to-amber-50/50",
        error:
          "border-red-200 bg-gradient-to-br from-red-50 to-red-50/50",
        transparent: "border-slate-100 bg-transparent",
      },
      size: {
        sm: "p-4",
        md: "p-6",
        lg: "p-8",
        xl: "p-10",
      },
      hover: {
        none: "",
        subtle: "hover:border-slate-300 hover:shadow-md",
        glow: "hover:border-primary/30 hover:shadow-[0_0_20px_rgba(37,99,235,0.08)]",
        lift: "hover:-translate-y-1 hover:shadow-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "md",
      hover: "none",
    },
  }
);

export interface GlassPanelProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof glassPanelVariants> {}

const GlassPanel = React.forwardRef<HTMLDivElement, GlassPanelProps>(
  ({ className, variant, size, hover, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(glassPanelVariants({ variant, size, hover }), className)}
        {...props}
      />
    );
  }
);
GlassPanel.displayName = "GlassPanel";

export { GlassPanel, glassPanelVariants };
